import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Card, 
  CardContent, 
  Container,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Fade,
  Grow,
  Slide,
  Stack,
  CircularProgress
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import meetingService from '../services/meetingService';
import transcriptionService from '../services/transcriptionService';
import summaryService from '../services/summaryService';
import { getApplicationStats, getUserStats } from '../firebase/services/statisticsService';
import { isUserAdmin } from '../firebase/services/userService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import appConfig from '../config/appConfig';
import NewMeetingDialog from '../components/common/NewMeetingDialog';
import UniversalCard from '../components/common/UniversalCard';
import { primaryGradient } from '../theme';

// Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MicIcon from '@mui/icons-material/Mic';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TimerIcon from '@mui/icons-material/Timer';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const countUpAnimation = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Styled Components avec design glassmorphism
const GlassCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(248,250,255,0.85) 100%)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: '24px',
  boxShadow: '0 16px 40px rgba(63, 81, 181, 0.1), 0 8px 24px rgba(63, 81, 181, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1), transparent, rgba(255,255,255,0.1))',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 24px 60px rgba(63, 81, 181, 0.15), 0 12px 32px rgba(63, 81, 181, 0.1)',
    '&::before': {
      opacity: 1,
    }
  }
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: primaryGradient,
  padding: theme.spacing(6, 0, 4),
  position: 'relative',
  marginLeft: '-24px', // Compenser le padding gauche du MainLayout
  marginRight: '-24px', // Compenser le padding droit du MainLayout
  paddingLeft: '24px', // Remettre le padding interne
  paddingRight: '24px', // Remettre le padding interne
  borderRadius: '0 0 32px 32px',
  overflow: 'hidden',
  minHeight: '30vh',
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 20px 40px rgba(63, 81, 181, 0.25), 0 10px 20px rgba(63, 81, 181, 0.15)',
  // Zone de transition douce avec l'AppBar
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '10px',
    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, transparent 100%)',
    zIndex: 1
  },
  // Effets décoratifs
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
      linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)
    `,
    zIndex: 1
  }
}));

// Composant décoratif flottant pour la HeroSection
const FloatingDecoration = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px', // Plus bas pour ne pas interférer avec la transition
  right: '8%', // Légèrement vers l'intérieur
  width: '80px', // Plus petit
  height: '80px', // Plus petit
  background: 'linear-gradient(45deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))',
  borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
  transform: 'rotate(45deg)',
  animation: `${floatAnimation} 8s ease-in-out infinite`,
  zIndex: 2, // Au-dessus des autres effets
  opacity: 0.8,
  filter: 'blur(0.5px)',
  [theme.breakpoints.down('md')]: {
    display: 'none' // Masquer sur mobile pour éviter les problèmes
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '25%',
    left: '25%',
    width: '50%',
    height: '50%',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '50%',
    animation: `${pulseAnimation} 4s ease-in-out infinite`,
  }
}));

const StatCard = styled(GlassCard)(({ theme, color }) => ({
  height: '180px',
  cursor: 'pointer',
  '&:hover': {
    animation: `${pulseAnimation} 0.6s ease-in-out`,
  },
  '& .stat-icon': {
    width: 64,
    height: 64,
    borderRadius: '20px',
    background: `linear-gradient(135deg, ${color}20, ${color}40)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
    color: color,
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
    animation: `${floatAnimation} 3s ease-in-out infinite`,
  },
  '& .stat-number': {
    fontSize: '2.5rem',
    fontWeight: 800,
    background: `linear-gradient(135deg, ${color}, ${color}80)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${countUpAnimation} 1s ease-out`,
  }
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingBottom: '56.25%', // 16:9 aspect ratio
  height: 0,
  overflow: 'hidden',
  borderRadius: '24px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  '& iframe': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '24px',
  }
}));

const CountUpNumber = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!value) return;
    
    let startTime;
    const startValue = 0;
    const endValue = typeof value === 'number' ? value : parseInt(value) || 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutCubic);
      
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [stats, setStats] = useState(null);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState({
    totalMeetings: 0,
    totalTranscriptions: 0,
    totalSummaries: 0
  });

  // États pour la dialog de création de réunion
  const [newMeetingDialogOpen, setNewMeetingDialogOpen] = useState(false);

  // États pour les données temps réel des réunions récentes
  const [transcriptions, setTranscriptions] = useState({});
  const [summaries, setSummaries] = useState({});

  // Utiliser useRef pour éviter les rechargements multiples
  const initializationRef = useRef(false);
  const userKey = useRef('');
  const unsubscribersRef = useRef({});

  useEffect(() => {
    const currentKey = currentUser?.uid || '';
    
    // Si l'utilisateur change, réinitialiser
    if (userKey.current !== currentKey) {
      userKey.current = currentKey;
      initializationRef.current = false;
        
      // Nettoyer les anciens listeners
      Object.values(unsubscribersRef.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      unsubscribersRef.current = {};
    }
    
    let isMounted = true; // Flag pour éviter les setState après unmount
    
    const loadData = async () => {
      if (!currentUser?.uid || initializationRef.current || !isMounted) return;
      
      initializationRef.current = true; // Marquer comme en cours d'initialisation
      
      try {
        if (isMounted) setLoading(true);
        
        const userId = currentUser.uid;
        const isAdmin = await isUserAdmin(userId);
        
        let appStats;
        if (isAdmin) {
          // Admin : statistiques complètes (avec agrégations)
          try {
            appStats = await getApplicationStats();
          } catch (adminStatsError) {
            addSystemNotification('❌ Erreur lors du chargement des statistiques admin:', 'error');
            appStats = await getUserStats(userId);
          }
        } else {
          // Utilisateur normal : statistiques limitées (sans agrégations)
          try {
            appStats = await getUserStats(userId);
          } catch (userStatsError) {
            addSystemNotification('❌ Erreur lors du chargement des statistiques utilisateur:', 'error');
            appStats = {
              userMeetings: 0,
              userTranscriptions: 0,
              userSummaries: 0,
              recentMeetings: [],
              isLimitedView: true,
              error: userStatsError.message,
              generatedAt: new Date()
            };
          }
        }
        
        if (isMounted) {
        setStats(appStats);
        }

        // Configurer les listeners pour les réunions récentes
        if (userId && isMounted) {
          // Écouter les réunions en temps réel
          const unsubscribeMeetings = meetingService.onUserMeetings(userId, (meetings) => {
            if (!isMounted) return;
            
            const recentUserMeetings = meetings.slice(0, 5);
            setRecentMeetings(recentUserMeetings);
            
            setUserStats(prev => ({
              ...prev,
              totalMeetings: meetings.length,
              totalTranscriptions: meetings.filter(m => m.transcriptionId).length
            }));

            // Nettoyer les anciens listeners de transcriptions/résumés
            Object.keys(unsubscribersRef.current).forEach(key => {
              if (key.startsWith('transcription_') || key.startsWith('summary_')) {
                const unsubscribe = unsubscribersRef.current[key];
              if (typeof unsubscribe === 'function') unsubscribe();
                delete unsubscribersRef.current[key];
              }
            });
            
            setTranscriptions({});
            setSummaries({});
            
            // Configurer l'écoute pour les transcriptions et résumés
            recentUserMeetings.forEach(meeting => {
              if (meeting.transcriptionId && isMounted) {
                // Listener transcription
                const unsubTranscription = transcriptionService.onTranscriptionById(
                  meeting.transcriptionId,
                  (transcription) => {
                    if (isMounted) {
                    setTranscriptions(prev => ({
                      ...prev,
                      [meeting.id]: transcription
                    }));
                    }
                  }
                );
                unsubscribersRef.current[`transcription_${meeting.id}`] = unsubTranscription;
                
                // Listener résumé
                const unsubSummary = summaryService.onSummaryByTranscriptionId(
                  meeting.transcriptionId,
                  (summary) => {
                    if (isMounted) {
                    setSummaries(prev => ({
                      ...prev,
                      [meeting.id]: summary
                    }));
                    }
                  }
                );
                unsubscribersRef.current[`summary_${meeting.id}`] = unsubSummary;
              }
            });
          });
          unsubscribersRef.current.meetings = unsubscribeMeetings;

          // Écouter tous les résumés pour calculer le total
          const unsubscribeSummaries = summaryService.onUserSummaries(userId, (allSummaries) => {
            if (!isMounted) return;
            const completedSummaries = allSummaries.filter(s => s.status === 'Terminé').length;
            setUserStats(prev => ({
              ...prev,
              totalSummaries: completedSummaries
            }));
          });
          unsubscribersRef.current.allSummaries = unsubscribeSummaries;
        }
      } catch (error) {
        addSystemNotification('❌ Erreur lors du chargement des données:', 'error');
        
        if (isMounted) {
        setStats({
          userMeetings: 0,
          userTranscriptions: 0,
          userSummaries: 0,
          recentMeetings: [],
          isLimitedView: true,
          error: error.message,
          generatedAt: new Date()
        });
        }
      } finally {
        if (isMounted) {
        setLoading(false);
        }
      }
    };

    if (currentUser?.uid && !initializationRef.current) {
      // Délai pour éviter les appels multiples
      const timeoutId = setTimeout(() => {
        if (!initializationRef.current) {
    loadData();
        }
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        isMounted = false;
      };
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
      // Les listeners seront nettoyés lors du changement d'utilisateur ou unmount du composant
    };
  }, [currentUser?.uid]);

  // Cleanup global à l'unmount du composant
  useEffect(() => {
    return () => {
      Object.values(unsubscribersRef.current).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
      unsubscribersRef.current = {};
    };
  }, []);

  const getUserDisplayName = () => {
    if (!currentUser) return 'Utilisateur';
    return currentUser.displayName || 
           currentUser.profile?.displayName || 
           currentUser.email?.split('@')[0] || 
           'Utilisateur';
  };

  const getUserAvatar = () => {
    const photoURL = currentUser?.photoURL || currentUser?.profile?.photoURL;
    if (photoURL) {
      return photoURL;
    }
    return null;
  };

  // Ouvrir le dialog de création de réunion
  const handleNewMeeting = () => {
    setNewMeetingDialogOpen(true);
  };

  // Callback de succès de création de réunion
  const handleNewMeetingSuccess = (meetingId) => {
    setNewMeetingDialogOpen(false);
    // Naviguer vers la liste des réunions
    navigate('/meetings');
  };

  // Gestion du clic sur les cartes de statistiques
  const handleStatCardClick = (statTitle) => {
    switch (statTitle) {
      case 'Mes Réunions':
        navigate('/meetings');
        break;
      default:
        // Pas de navigation pour les autres cartes pour le moment
        break;
    }
  };

  const statCards = [
    {
      title: 'Mes Réunions',
      value: userStats.totalMeetings,
      icon: <MicIcon />,
      color: '#3F51B5',
      subtitle: 'Total créées'
    },
    {
      title: 'Transcriptions',
      value: userStats.totalTranscriptions,
      icon: <DescriptionIcon />,
      color: '#FF6B6B',
      subtitle: 'Effectuées'
    },
    {
      title: 'Résumés Générés',
      value: userStats.totalSummaries,
      icon: <AutoAwesomeIcon />,
      color: '#4CAF50',
      subtitle: 'IA générative'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#fafbff'
    }}>
      {/* Hero Section */}
      <HeroSection>
        {/* Élément décoratif flottant */}
        <FloatingDecoration />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                {getUserAvatar() ? (
                  <Avatar 
                    src={getUserAvatar()} 
              sx={{ 
                      width: 56, 
                      height: 56, 
                      mr: 1.5,
                      border: '2px solid rgba(255,255,255,0.3)',
                      animation: `${floatAnimation} 4s ease-in-out infinite`,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                    }} 
                  />
                ) : (
                  <Avatar sx={{ 
                    width: 56, 
                    height: 56, 
                    mr: 1.5, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: '1.5rem',
                    border: '2px solid rgba(255,255,255,0.3)',
                    animation: `${floatAnimation} 4s ease-in-out infinite`,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                  }}>
                    {getUserDisplayName().charAt(0).toUpperCase()}
                  </Avatar>
                )}
        </Box>
              
              <Typography 
                variant={isMobile ? "h6" : "h4"} 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  mb: 1,
                  textShadow: '0 3px 10px rgba(0,0,0,0.4)',
                  letterSpacing: '-0.02em'
                }}
              >
                Bonjour {getUserDisplayName()} ! 👋
                  </Typography>

              <Typography 
                variant={isMobile ? "body2" : "body1"} 
          sx={{ 
                  mb: 2, 
                  opacity: 0.95,
                  fontWeight: 400,
                  textShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  maxWidth: 450,
                  mx: 'auto'
          }}
        >
                Prêt à transformer vos réunions en insights puissants ?
              </Typography>
              
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleNewMeeting}
                sx={{ 
                  px: 4,
                  py: 1.2,
                  borderRadius: '50px',
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                    borderColor: 'rgba(255,255,255,0.6)'
                  }
                }}
                endIcon={<ArrowForwardIcon />}
              >
                Nouvelle Réunion
              </Button>
            </Box>
          </Fade>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mt: 2, position: 'relative', zIndex: 2 }}>
        {/* Statistiques utilisateur */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={stat.title}>
              <Grow in timeout={1000 + index * 200}>
                <StatCard color={stat.color} onClick={() => handleStatCardClick(stat.title)}>
                  <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box className="stat-icon">
                      {stat.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h3" className="stat-number">
                      <CountUpNumber value={stat.value} />
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      {stat.subtitle}
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grow>
            </Grid>
          ))}
          </Grid>

        {/* Section avec 2 colonnes */}
        <Grid container spacing={4} sx={{ mb: 5 }}>
          {/* Dernières réunions */}
          <Grid item xs={12} md={6}>
            <Slide direction="right" in timeout={1200}>
              <GlassCard sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <CalendarTodayIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Dernières Réunions
          </Typography>
                  </Box>
                  
                  {loading ? (
                    <Box sx={{ py: 4 }}>
                      <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
                      <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                        Chargement...
          </Typography>
        </Box>
                  ) : recentMeetings.length > 0 ? (
                    <Box sx={{ 
                      maxHeight: 400, 
                      overflow: 'auto',
                      overflowX: 'hidden', // Éviter le scroll horizontal
                      scrollBehavior: 'smooth',
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(63, 81, 181, 0.3)',
                        borderRadius: '3px',
                        '&:hover': {
                          background: 'rgba(63, 81, 181, 0.5)',
                        }
                      },
                      // Assurer que le scroll fonctionne correctement
                      WebkitOverflowScrolling: 'touch',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {recentMeetings.map((meeting, index) => {
                        const transcription = transcriptions[meeting.id];
                        const summary = summaries[meeting.id];

                        return (
                          <Fade in timeout={1500 + index * 100} key={meeting.id}>
                            <UniversalCard
                              type="meeting"
                              item={meeting}
                              transcription={transcription}
                              summary={summary}
                              actions={[
                                {
                                  type: 'view',
                                  icon: <VisibilityIcon fontSize="small" />,
                                  tooltip: 'Voir la réunion',
                                  color: 'info'
                                }
                              ]}
                              onClick={() => navigate(`/meetings/${meeting.id}`)}
                            />
                          </Fade>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <MicIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                      <Typography variant="h6" gutterBottom>
                        Aucune réunion encore
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3 }}>
                        Créez votre première réunion pour commencer !
                      </Typography>
          <Button 
                        variant="contained" 
                        onClick={() => navigate('/meetings')}
                        sx={{ borderRadius: '20px' }}
          >
                        Créer une réunion
          </Button>
                    </Box>
                  )}
                  
                  {recentMeetings.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Button 
                        variant="outlined" 
                        onClick={() => navigate('/meetings')}
                        sx={{ borderRadius: '20px' }}
                        endIcon={<ArrowForwardIcon />}
                      >
                        Voir toutes les réunions
                      </Button>
          </Box>
                  )}
                </CardContent>
              </GlassCard>
            </Slide>
          </Grid>

          {/* Vidéo tutoriel */}
          <Grid item xs={12} md={6}>
            <Slide direction="left" in timeout={1200}>
              <GlassCard sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PlayArrowIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Guide d'utilisation
            </Typography>
          </Box>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Découvrez comment tirer le meilleur parti de TranscriptionAI en quelques minutes.
                  </Typography>
                  
                  <VideoContainer>
                    <iframe
                      src="https://www.loom.com/embed/a4b64668ac6c4aaea9165b2470ce7e2d?sid=bf8d3c1a-1e4b-4c2e-9a15-2b8e3f3a6c5d"
                      title="Guide d'utilisation TranscriptionAI"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                />
                  </VideoContainer>
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Apprenez à créer des transcriptions et générer des résumés intelligents
            </Typography>
          </Box>
                </CardContent>
              </GlassCard>
            </Slide>
          </Grid>
        </Grid>

        {/* Section de progression */}
        <Fade in timeout={1600}>
          <GlassCard sx={{ mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                  Votre progression
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Continuez à utiliser l'outil de Transcription pour maximiser vos gains de productivité
            </Typography>
          </Box>

              <Grid container spacing={4} sx={{ textAlign: 'center' }}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                      <CountUpNumber value={userStats.totalMeetings * 30} />min
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Temps économisé
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Grâce à la transcription automatique
            </Typography>
          </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: 'success.main', mb: 1 }}>
                      <CountUpNumber value={userStats.totalSummaries * 10} />min
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Lecture rapide
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avec les résumés intelligents
            </Typography>
          </Box>
                </Grid>
                <Grid item xs={12} md={4}>
          <Box>
                    <Typography variant="h2" sx={{ fontWeight: 800, color: 'warning.main', mb: 1 }}>
                      {userStats.totalMeetings > 0 ? '95' : '0'}%
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Précision IA
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fiabilité des transcriptions
            </Typography>
          </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </Fade>

        {/* Footer / Disclaimer */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4, 
            borderTop: '1px solid rgba(63, 81, 181, 0.1)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(248,250,255,0.3) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px 20px 0 0',
            mt: 4
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            © {new Date().getFullYear()} {appConfig.appName} - Développé par <strong>{appConfig.developer}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Version {appConfig.version}
          </Typography>
        </Box>
      </Container>

      {/* Dialog de création de nouvelle réunion */}
      <NewMeetingDialog
        open={newMeetingDialogOpen}
        onClose={() => setNewMeetingDialogOpen(false)}
        onSuccess={handleNewMeetingSuccess}
      />
    </Box>
  );
};

export default HomePage; 